import classnames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { TertiaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import RichText from 'components/RichText';
import { SubBold } from 'components/Sub';
import { IMAGE_RATIOS, IMAGE_WIDTHS, IMAGE_BG_COLOR } from 'constants/images';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const InfoCardMobile = ({ image, heading, subText }) => {
  const [isOpen, setOverlayView] = useState(false);

  const handleToggleSection = () => {
    const newValue = !isOpen;
    setOverlayView(newValue);
  };

  return (
    <div className={classnames('InfoCardMobile', { 'InfoCardMobile--active': isOpen })}>
      <div className="InfoCardMobile__card">
        <div className="InfoCardMobile__image-wrapper">
          <Image
            className="InfoCardMobile__image"
            src={get(image, 'src')}
            alt={get(image, 'alt')}
            transformations={{ backgroundColor: IMAGE_BG_COLOR }}
            width={IMAGE_WIDTHS.W_1000}
            ratio={IMAGE_RATIOS.R_1x1}
          />
          <div className="InfoCardMobile__overlay-header">
            <TertiaryHeading className="InfoCardMobile__overlay-heading">{heading}</TertiaryHeading>
          </div>
        </div>
        <div className="InfoCardMobile__overlay" />
        <div className="InfoCardMobile__overlay-subheader">
          <RichText content={subText} />
        </div>
        <div className="InfoCardMobile__header--fake">
          <TertiaryHeading className="InfoCardMobile__overlay-heading">{heading}</TertiaryHeading>
        </div>
      </div>
      <div className="InfoCardMobile__toggle">
        <button
          type="button"
          onClick={handleToggleSection}
          className="InfoCardMobile__toggle-button"
          aria-label={
            isOpen ? I18n.localize('carousel.learnLess') : I18n.localize('carousel.learnMore')
          }
        >
          <SubBold className="InfoCardMobile__toggle-button-text">
            {isOpen ? I18n.localize('carousel.learnLess') : I18n.localize('carousel.learnMore')}
          </SubBold>
          <i className="InfoCardMobile__toggle-button-icon" />
        </button>
      </div>
      <style jsx>{`
        .InfoCardMobile__card {
          position: relative;
          flex-grow: 1;
        }

        .InfoCardMobile {
          width: 100%;
          overflow: hidden;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          text-decoration: none;
          background-color: ${Theme.get('colors-white')};
          box-shadow: 0 8px ${Theme.get('spacing') * 4}px 0 rgba(0, 0, 0, 0.12);
        }

        .InfoCardMobile__image-wrapper {
          width: 100%;
          background-color: ${IMAGE_BG_COLOR};
          position: relative;
        }

        .InfoCardMobile__overlay {
          position: absolute;
          opacity: 0;
          background-color: white;
          height: 100%;
          width: 100%;
          top: 0;
          transition: all ${Theme.get('transition-duration-ms')}
            ${Theme.get('transition-timing-ease-out-quad')};
        }

        .InfoCardMobile__overlay-header,
        .InfoCardMobile__header--fake {
          padding: ${Theme.get('spacing') * 3}px;
        }

        .InfoCardMobile__overlay-header {
          position: absolute;
          z-index: 1;
          top: 100%;
          bottom: 0;
          transition: all ${Theme.get('transition-duration-ms')}
            ${Theme.get('transition-timing-ease-out-quad')};
          width: 100%;
        }

        .InfoCardMobile__overlay-subheader {
          position: absolute;
          z-index: 1;
          bottom: 0px;
          padding: ${Theme.get('spacing') * 2}px ${Theme.get('spacing') * 3}px 0;
          opacity: 0;
          transition: all ${Theme.get('transition-duration-ms')}
            ${Theme.get('transition-timing-ease-out-quad')};
          width: 100%;
        }

        .InfoCardMobile__header--fake {
          visibility: hidden;
        }

        .InfoCardMobile__toggle-button {
          position: relative;
          z-index: 2;
          appearance: none;
          width: 100%;
          padding: ${Theme.get('spacing') * 1.5}px;
          padding-left: ${Theme.get('spacing') * 3}px;
          cursor: pointer;
          background-color: ${Theme.get('colors-white')};
          outline: none;
          border: 0;
          border-top: solid 1px ${Theme.get('colors-black-20')};
          text-align: left;
        }

        .InfoCardMobile__toggle-button-icon:before,
        .InfoCardMobile__toggle-button-icon:after {
          content: '';
          position: absolute;
          border-left: 2px solid ${Theme.get('colors-black')};
          top: ${Theme.get('spacing') * 2 + 1}px;
          right: ${Theme.get('spacing') * 4}px;
          height: ${Theme.get('spacing') * 2}px;
          transition: transform ${Theme.get('transition-duration-ms')};
        }

        .InfoCardMobile__toggle-button-icon:before {
          transform: rotate(0);
        }

        .InfoCardMobile__toggle-button-icon:after {
          transform: rotate(90deg);
        }

        .InfoCardMobile--active .InfoCardMobile__overlay-header {
          top: 0;
        }

        .InfoCardMobile--active .InfoCardMobile__overlay-subheader {
          opacity: 1;
          bottom: 12px;
        }

        .InfoCardMobile--active .InfoCardMobile__overlay {
          opacity: 1;
        }

        .InfoCardMobile--active .InfoCardMobile__toggle-button-icon:before {
          transform: rotate(45deg);
        }

        .InfoCardMobile--active .InfoCardMobile__toggle-button-icon:after {
          transform: rotate(135deg);
        }
      `}</style>
      <style jsx global>
        {`
          .InfoCardMobile__overlay-subheader p.Paragraph {
            margin: 0;
          }

          .InfoCardMobile__overlay-heading.typography--tertiary-heading {
            font-size: 16px;
          }
        `}
      </style>
    </div>
  );
};

InfoCardMobile.propTypes = {
  image: PropTypes.shape({ src: PropTypes.string.isRequired, alt: PropTypes.string }).isRequired,
  heading: PropTypes.string.isRequired,
  subText: PropTypes.shape({}).isRequired,
};

export default InfoCardMobile;
